// CreatorBlitzPayHeader.jsx
import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Stack,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Divider,
  useMediaQuery,
  Card,
  CardContent,
  Avatar,
  Chip,
  Fade,
} from "@mui/material";
import {
  ReceiptLong as ReceiptIcon, // Updated icon
  ArrowOutward as TransferIcon, // Updated icon
  Close as CloseIcon,
  Settings as SettingsIcon,
  MoreVert as MoreVertIcon,
  SupportAgent as ChatIcon, // Updated icon
  AccountBalanceWallet as WalletIcon,
  History as HistoryIcon,
  VerifiedUser as VerifiedIcon,
  GppBad as UnverifiedIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useTheme, alpha } from '@mui/material/styles';

const CreatorBlitzPayHeader = ({ 
  currentBalance,
  balanceLoading,
  onShowBalanceLedger,
  onOpenCEOChat,
  walletCount = 0,
  hasVerifiedWallets = false,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // AppShell spacing constant
  const APPSHELL_HEIGHT = 86;
  
  // State
  const [drawerOpen, setDrawerOpen] = useState(false);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(value || 0);
  };

  // Handlers
  const handleDrawerToggle = () => setDrawerOpen(!drawerOpen);
  const handleDrawerAction = (action) => {
    if (action) action();
    setDrawerOpen(false);
  };

  // --- Components ---

  // 1. The "Hero" Balance Card (Credit Card Style)
  const BalanceCard = () => (
    <Box
      onClick={onShowBalanceLedger}
      sx={{
        position: 'relative',
        width: '100%',
        overflow: 'hidden',
        borderRadius: 4,
        cursor: 'pointer',
        transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:active': { transform: 'scale(0.98)' },
        // Mesh Gradient Background
        background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 50%, ${theme.palette.secondary.main} 100%)`,
        boxShadow: `0 8px 32px 0 ${alpha(theme.palette.primary.main, 0.3)}`,
      }}
    >
      {/* Decorative Circles/Glows */}
      <Box sx={{
        position: 'absolute', top: -50, right: -50, width: 200, height: 200,
        borderRadius: '50%', background: alpha('#fff', 0.1), filter: 'blur(40px)',
      }} />
      <Box sx={{
        position: 'absolute', bottom: -30, left: -30, width: 150, height: 150,
        borderRadius: '50%', background: alpha(theme.palette.secondary.light, 0.2), filter: 'blur(30px)',
      }} />

      <CardContent sx={{ position: 'relative', zIndex: 1, p: 3, color: 'white' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }}>
          <Box 
            sx={{ 
              p: 1, 
              borderRadius: 2, 
              bgcolor: alpha('#fff', 0.15),
              backdropFilter: 'blur(10px)',
              border: `1px solid ${alpha('#fff', 0.2)}`
            }}
          >
            <WalletIcon sx={{ color: '#fff' }} />
          </Box>
          <Chip 
            label="Total Balance" 
            size="small" 
            sx={{ 
              bgcolor: alpha('#000', 0.2), 
              color: 'rgba(255,255,255,0.9)', 
              border: 'none',
              fontWeight: 500 
            }} 
          />
        </Stack>

        <Box>
          <Typography variant="h3" fontWeight="800" sx={{ mb: 0.5, letterSpacing: '-0.02em', fontSize: { xs: '2rem', sm: '2.5rem' } }}>
            {balanceLoading ? "..." : formatCurrency(currentBalance)}
          </Typography>
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ opacity: 0.8 }}>
            <HistoryIcon fontSize="small" sx={{ fontSize: 16 }} />
            <Typography variant="body2" fontWeight={500}>
              Tap to view ledger history
            </Typography>
          </Stack>
        </Box>
      </CardContent>
    </Box>
  );

  // 2. Action Button Component (Squircle styling)
  const ActionButton = ({ icon: Icon, label, subLabel, color, onClick }) => (
    <Card
      elevation={0}
      onClick={onClick}
      sx={{
        flex: 1,
        borderRadius: 3,
        bgcolor: alpha(theme.palette.background.paper, 0.6),
        backdropFilter: 'blur(20px)',
        border: '1px solid',
        borderColor: 'divider',
        transition: 'all 0.2s',
        cursor: 'pointer',
        '&:hover': {
          borderColor: color,
          bgcolor: alpha(color, 0.05),
          transform: 'translateY(-2px)',
        },
        '&:active': { transform: 'scale(0.97)' }
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box sx={{
          width: 42, height: 42, borderRadius: '14px',
          bgcolor: alpha(color, 0.1), color: color,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          mb: 1.5
        }}>
          <Icon />
        </Box>
        <Typography variant="subtitle2" fontWeight={700} sx={{ lineHeight: 1.2 }}>
          {label}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
          {subLabel}
        </Typography>
      </CardContent>
    </Card>
  );

  // 3. Desktop Actions
  const renderDesktopActions = () => (
    <Stack direction="row" spacing={2} alignItems="center">
      <Button
        variant="contained"
        startIcon={<TransferIcon />}
        onClick={() => navigate("/creatorconnect/internal-transfer")}
        sx={{ borderRadius: 2.5, px: 3, py: 1.2, fontWeight: 600, textTransform: 'none', boxShadow: theme.shadows[2] }}
      >
        Pay Someone
      </Button>
      <Button
        variant="outlined"
        startIcon={<ReceiptIcon />}
        onClick={() => navigate("/creatorinvoicing")}
        sx={{ borderRadius: 2.5, px: 3, py: 1.2, fontWeight: 600, textTransform: 'none', borderWidth: 1.5 }}
      >
        Request Balance
      </Button>
      <Button
        color="inherit"
        startIcon={<ChatIcon />}
        onClick={onOpenCEOChat}
        sx={{ borderRadius: 2.5, px: 3, textTransform: 'none' }}
      >
        CEO Support
      </Button>
      <IconButton 
        onClick={() => navigate('/creatorconnect/edit?tab=wallets')}
        sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2.5 }}
      >
        <SettingsIcon />
      </IconButton>
    </Stack>
  );

  // 4. Mobile Layout
  const renderMobileLayout = () => (
    <Box sx={{ width: '100%', mt: 2 }}>
      <Stack spacing={2}>
        <BalanceCard />

        {/* Action Grid */}
        <Stack direction="row" spacing={1.5}>
          <ActionButton 
            icon={TransferIcon}
            label="Transfer"
            subLabel="Pay others"
            color={theme.palette.primary.main}
            onClick={() => navigate("/creatorconnect/internal-transfer")}
          />
          <ActionButton 
            icon={ReceiptIcon}
            label="Request"
            subLabel="Create invoice"
            color={theme.palette.secondary.main}
            onClick={() => navigate("/creatorinvoicing")}
          />
        </Stack>

        {/* Status Bar */}
        {walletCount > 0 && (
          <Fade in={true}>
            <Box
              sx={{
                p: 1.5,
                borderRadius: 3,
                bgcolor: hasVerifiedWallets ? alpha(theme.palette.success.main, 0.08) : alpha(theme.palette.warning.main, 0.08),
                border: '1px solid',
                borderColor: hasVerifiedWallets ? alpha(theme.palette.success.main, 0.2) : alpha(theme.palette.warning.main, 0.2),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Box sx={{ 
                  p: 0.5, borderRadius: '50%', 
                  bgcolor: hasVerifiedWallets ? theme.palette.success.main : theme.palette.warning.main,
                  color: '#fff', display: 'flex' 
                }}>
                  {hasVerifiedWallets ? <VerifiedIcon sx={{ fontSize: 16 }} /> : <UnverifiedIcon sx={{ fontSize: 16 }} />}
                </Box>
                <Typography variant="body2" fontWeight={600} sx={{ color: 'text.primary', fontSize: '0.85rem' }}>
                   {walletCount} {walletCount === 1 ? 'Wallet' : 'Wallets'} Connected
                </Typography>
              </Stack>
              <Button 
                size="small" 
                onClick={() => navigate('/creatorconnect/edit?tab=wallets')}
                sx={{ 
                  minWidth: 0, 
                  p: 0.5, 
                  fontSize: '0.75rem', 
                  color: hasVerifiedWallets ? 'success.dark' : 'warning.dark' 
                }}
              >
                Manage
              </Button>
            </Box>
          </Fade>
        )}
      </Stack>
    </Box>
  );

  return (
    <Box sx={{ 
      px: { xs: 2, md: 4 },
      pt: { xs: 2, md: 3 },
      pb: { xs: 2, md: 2 },
    }}>
      <Stack 
        direction="row" 
        justifyContent="space-between" 
        alignItems="center"
        sx={{ mb: isMobile ? 0 : 3 }}
      >
        <Box>
          <Typography 
            variant="h4" 
            fontWeight="800" 
            sx={{ 
              fontSize: { xs: '1.5rem', sm: '2rem' },
              background: `linear-gradient(45deg, ${theme.palette.text.primary} 30%, ${theme.palette.primary.main} 90%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            BlitzPay
          </Typography>
          {!isMobile && (
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              Financial Command Center
            </Typography>
          )}
        </Box>

        {isMobile && (
          <IconButton
            onClick={handleDrawerToggle}
            sx={{
              bgcolor: alpha(theme.palette.background.paper, 0.5),
              backdropFilter: 'blur(8px)',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '12px',
            }}
          >
            <MoreVertIcon />
          </IconButton>
        )}

        {!isMobile && renderDesktopActions()}
      </Stack>

      {isMobile && renderMobileLayout()}

      {/* --- Sleek Mobile Drawer --- */}
      <Drawer
        anchor="bottom"
        open={drawerOpen}
        onClose={handleDrawerToggle}
        PaperProps={{
          sx: {
            borderTopLeftRadius: 28,
            borderTopRightRadius: 28,
            bgcolor: theme.palette.background.default,
            backgroundImage: 'none',
            pb: `${APPSHELL_HEIGHT + 20}px`, // Safe area padding
            boxShadow: '0 -10px 40px rgba(0,0,0,0.1)',
          }
        }}
        ModalProps={{
          BackdropProps: {
            sx: { backdropFilter: 'blur(4px)', backgroundColor: 'rgba(0,0,0,0.4)' }
          }
        }}
      >
        <Box sx={{ px: 3, pt: 1.5, pb: 2 }}>
          {/* Drawer Handle */}
          <Box sx={{ width: 40, height: 4, bgcolor: 'divider', borderRadius: 2, mx: 'auto', mb: 3 }} />
          
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6" fontWeight={800}>Menu</Typography>
            <IconButton size="small" onClick={handleDrawerToggle} sx={{ bgcolor: 'action.hover' }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>

          <List disablePadding>
            <ListItem disablePadding sx={{ mb: 1.5 }}>
              <ListItemButton
                onClick={() => handleDrawerAction(onOpenCEOChat)}
                sx={{ borderRadius: 3, bgcolor: alpha(theme.palette.primary.main, 0.05), py: 2 }}
              >
                <ListItemIcon>
                  <ChatIcon color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="VIP Support" 
                  secondary="Direct line to the CEO"
                  primaryTypographyProps={{ fontWeight: 700 }}
                />
              </ListItemButton>
            </ListItem>

            <ListItem disablePadding>
              <ListItemButton
                onClick={() => handleDrawerAction(() => navigate('/creatorconnect/edit?tab=wallets'))}
                sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', py: 2 }}
              >
                <ListItemIcon>
                  <SettingsIcon />
                </ListItemIcon>
                <ListItemText 
                  primary="Settings" 
                  secondary="Wallets & Preferences"
                  primaryTypographyProps={{ fontWeight: 600 }}
                />
              </ListItemButton>
            </ListItem>
          </List>
        </Box>
      </Drawer>
    </Box>
  );
};

export default CreatorBlitzPayHeader;